import { h, FunctionComponent } from "preact";
import { Ref, useCallback, useContext, useEffect, useMemo, useRef, useState } from "preact/hooks";

import { FullView, FULL_VIEW_MARGIN_P } from "./full_view";
import { Tile, TileComponent } from "./tile";
import { assert } from "../../utils/asserts";
import { RoutingContext } from "../routing_context";

import css from "./gallery.scss";

interface Props {
  tiles: Tile[];
  galleryId: string;
  selectedImageId?: string;
}

interface State {
  selectedTile: Tile | null;
  isTransitioningToFullView: boolean;
  isTransitioningFromFullView: boolean;
  showGallery: boolean;
}

const GALLERY_URL_STATE_SYMBOL = "_gallery_url_state";

function getFullViewTransitionEndStyle(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const scale =
    Math.min(window.innerWidth / rect.width, window.innerHeight / rect.height) *
    (1 - FULL_VIEW_MARGIN_P * 2);

  const translateX = window.innerWidth / 2 - rect.left - rect.width / 2;
  const translateY = window.innerHeight / 2 - rect.top - rect.height / 2;

  return {
    transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
  };
}

function getFullViewTransitionStartStyle(el: HTMLElement) {
  return {
    transform: "translate(0, 0) scale(1)",
  };
}

function useLazyRef<T>(initialValue: () => T): Ref<T> {
  const ref = useRef<T | undefined>(undefined);
  if (ref.current === undefined) ref.current = initialValue();

  return ref as Ref<T>;
}

function useTileScrollObserver() {
  const containerEl = useRef<HTMLDivElement>(null);

  const observedTilesRef = useLazyRef(() => new Set<HTMLDivElement>());
  const tileElementsRef = useLazyRef(() => new TilesElementMap());

  const handleTileIntersection = useCallback(
    (entry: IntersectionObserverEntry) => {
      const tileWrapperEl = entry.target as HTMLElement;
      const tileEl = tileWrapperEl.children.item(0)! as HTMLElement;
      const imageEl = tileEl.children.item(0)! as HTMLElement;

      const ratio =
        (entry.intersectionRect.bottom - entry.boundingClientRect.top) /
        entry.boundingClientRect.height;

      const t = (Math.min(Math.max(ratio, 0.2), 0.8) - 0.2) / 0.6;

      const scale = t * 0.4 + 0.6;
      const opacity = t;

      imageEl.style.opacity = `${opacity}`;
      tileEl.style.transform = `scale(${scale})`;

      tileElementsRef.current.setVisible(tileEl, entry.isIntersecting);
    },
    [tileElementsRef]
  );

  const intersectionObserverRef = useRef<IntersectionObserver>();
  useEffect(() => {
    if (BUILD_PLATFORM !== "browser") return;

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          handleTileIntersection(entry);
        }
      },
      {
        root: null,
        threshold: [0, ...getSteps(0.2, 0.8, 100), 1],
        rootMargin: "0px 0px -10% 0px",
      }
    );
    intersectionObserverRef.current = intersectionObserver;

    return () => {
      intersectionObserver.disconnect();
    };
  }, [handleTileIntersection]);

  // DONE?
  const updateObservedTiles = useCallback(() => {
    const intersectionObserver = intersectionObserverRef.current;
    if (!intersectionObserver || !containerEl.current) return;

    const children = containerEl.current.querySelectorAll(`.${css("tile-wrapper")}`);

    const observedTiles = observedTilesRef.current;
    const remainingTiles = new Set(observedTiles);
    for (let i = 0; i < children.length; i++) {
      const el = children.item(i) as HTMLDivElement;

      if (remainingTiles.has(el)) {
        remainingTiles.delete(el);
        continue;
      }

      observedTiles.add(el);
      intersectionObserver.observe(el);
    }

    for (const el of remainingTiles.values()) {
      observedTiles.delete(el);
      intersectionObserver.unobserve(el);
    }
  }, [observedTilesRef]);

  useEffect(() => {
    updateObservedTiles();
  });

  return { containerEl, tileElements: tileElementsRef.current };
}

function useSelectedTile(
  tiles: Tile[],
  selectedImageId: string | undefined,
  tileElements: TilesElementMap
) {
  const [showGallery, setShowGallery] = useState(!selectedImageId);
  const [isTransitioningToFullView, setIsTransitioningToFullView] = useState(false);
  const [isTransitioningFromFullView, setIsTransitioningFromFullView] = useState(false);
  const [fullViewTile, setFullViewTile] = useState<Tile | null>();
  const pendingTransitionAnimation = useRef<Animation>();

  const selectedTile = useMemo(
    () =>
      selectedImageId ? tiles.find((tile) => tile.image.id === selectedImageId) ?? null : null,
    [selectedImageId, tiles]
  );

  const [nextTile, previousTile] = useMemo(() => {
    if (!selectedTile) return [undefined, undefined];

    const i = tiles.indexOf(selectedTile);
    const nextTile = tiles[i + 1];
    const previousTile = tiles[i - 1];

    return [nextTile, previousTile];
  }, [tiles, selectedTile]);

  // Reset animation after transition to full view is complete.
  // This allows the transition back to be able to calculate the correct "end" coordinates.
  useEffect(() => {
    if (isTransitioningToFullView || isTransitioningFromFullView) return;
    if (!pendingTransitionAnimation.current) return;

    pendingTransitionAnimation.current.cancel();
    pendingTransitionAnimation.current = undefined;
  }, [isTransitioningToFullView, isTransitioningFromFullView]);

  useEffect(() => {
    if (selectedTile === fullViewTile) return;

    // Start animation
    if (selectedTile) {
      const tileElement = first(tileElements.getVisibleTileElements(selectedTile));

      if (tileElement && !fullViewTile) {
        // transition to full view
        tileElement.style.transform = "scale(1)";
        const animation = tileElement.animate(
          [
            getFullViewTransitionStartStyle(tileElement),
            getFullViewTransitionEndStyle(tileElement),
          ],
          { duration: 200, fill: "forwards", easing: "ease" }
        );

        animation.addEventListener("finish", () => {
          // Save this animation so that it can be deleted when the animation is done and
          // final state is rendered. If animation is deleted before that, we will flicker
          // back to the original view momentarly.
          pendingTransitionAnimation.current = animation;

          // Render final post-transition state.
          setIsTransitioningToFullView(false);
        });

        setFullViewTile(selectedTile);
        setIsTransitioningToFullView(true);
      } else {
        // skip transition
        setFullViewTile(selectedTile);
      }
    } else if (fullViewTile) {
      const tileElement = first(tileElements.getVisibleTileElements(fullViewTile));

      if (tileElement) {
        // transition from full view
        setIsTransitioningFromFullView(true);
        setShowGallery(true);

        const animation = tileElement.animate(
          [
            getFullViewTransitionEndStyle(tileElement),
            getFullViewTransitionStartStyle(tileElement),
          ],
          { duration: 200, fill: "forwards", easing: "ease" }
        );
        animation.addEventListener("finish", () => {
          /*
          ????
          if (intersectionObserver) {
            intersectionObserver
              .takeRecords()
              .forEach((entry) => handleTileIntersection(entry));
          }
          */

          // Render post-transition state
          setFullViewTile(null);
          setIsTransitioningFromFullView(false);

          // Delete this animation.
          // Note that deleting this animation does not need to wait for the above
          // state to be rendered.
          animation.cancel();
        });
      } else {
        // skip transition
        setFullViewTile(null);
        setShowGallery(true);
      }
    }
  }, [tileElements, fullViewTile, selectedTile]);

  return {
    fullViewTile,
    nextTile,
    previousTile,
    showGallery,
    isTransitioningFromFullView,
    isTransitioningToFullView,
  };
}

export const Gallery: FunctionComponent<Props> = (props) => {
  const { tiles, selectedImageId, galleryId } = props;

  // DONE
  const routing = useContext(RoutingContext);

  // DONE
  const handleFullViewClose = useCallback(() => {
    const urlState = window.history.state;
    if (urlState && GALLERY_URL_STATE_SYMBOL in urlState) {
      history.back();
    } else {
      const url = getUrlForSelectedTile(galleryId, undefined);
      routing.push(url);
    }
  }, [routing, galleryId]);

  const { containerEl, tileElements } = useTileScrollObserver();

  const {
    showGallery,
    fullViewTile,
    nextTile,
    previousTile,
    isTransitioningFromFullView,
    isTransitioningToFullView,
  } = useSelectedTile(tiles, selectedImageId, tileElements);

  let handleGoToNext;
  let handleGoToPrevious;

  handleGoToNext = useCallback(() => {
    if (!fullViewTile) return;
    const i = tiles.indexOf(fullViewTile);
    const tile = tiles[i + 1];
    const url = getUrlForSelectedTile(galleryId, tile);
    assert(routing);
    routing.replace(url, {
      [GALLERY_URL_STATE_SYMBOL]: true,
    });
  }, [routing, galleryId, tiles, fullViewTile]);

  handleGoToPrevious = useCallback(() => {
    if (!fullViewTile) return;
    const i = tiles.indexOf(fullViewTile);
    const tile = tiles[i - 1];
    const url = getUrlForSelectedTile(galleryId, tile);
    assert(routing);
    routing.replace(url, {
      [GALLERY_URL_STATE_SYMBOL]: true,
    });
  }, [routing, galleryId, tiles, fullViewTile]);

  // DONE
  const handleTileClick = useCallback(
    (tile: Tile) => {
      const url = getUrlForSelectedTile(galleryId, tile);
      assert(routing);
      routing.push(url, {
        [GALLERY_URL_STATE_SYMBOL]: true,
      });
    },
    [routing, galleryId]
  );

  const tileEls = [];
  if (showGallery) {
    for (const tile of tiles) {
      const isSelected = tile === fullViewTile;
      tileEls.push(
        <TileComponent
          class={css("tile-wrapper", {
            "transition-to-full":
              (isTransitioningToFullView || isTransitioningFromFullView) && isSelected,
          })}
          tile={tile}
          onClick={() => handleTileClick(tile)}
          tileElementRef={(tile: Tile) => tileElements.ref(tile)}
        />
      );
    }
  }

  let fullViewEl = null;
  if (fullViewTile) {
    fullViewEl = (
      <FullView
        tile={fullViewTile}
        inTransition={isTransitioningToFullView || isTransitioningFromFullView}
        onClose={handleFullViewClose}
        onGoToNext={nextTile ? handleGoToNext : undefined}
        onGoToPrevious={previousTile ? handleGoToPrevious : undefined}
      />
    );
  }

  let fullViewTransitionEl = null;
  if (isTransitioningToFullView) {
    fullViewTransitionEl = <div class={css("full-view-transition-bg")}></div>;
  } else if (isTransitioningFromFullView) {
    fullViewTransitionEl = <div class={css("full-view-reverse-transition-bg")}></div>;
  }

  return (
    <div ref={containerEl} class={css("tiles-container")}>
      {fullViewTransitionEl}
      {fullViewEl}
      <div class={css("phantom")}></div>
      {tileEls}
    </div>
  );
};

function getUrlForSelectedTile(galleryId: string, selectedTile?: Tile) {
  if (selectedTile) {
    return `/gallery/${galleryId}/${selectedTile.image.id}`;
  }

  return `/gallery/${galleryId}`;
}

export function getSteps(start: number, end: number, steps: number): number[] {
  const arr = new Array(steps).fill(0).map((_, i) => {
    return (i / (steps - 1)) * (end - start) + start;
  });
  arr.push(end);
  return arr;
}

interface TilesElementInfo {
  setElement: (el: HTMLElement | null) => void;
  currentElement: HTMLElement | null;
}

class TilesElementMap {
  private refs = new WeakMap<Tile, Map<number, TilesElementInfo>>();
  private visibleElements = new WeakSet<HTMLElement>();

  setVisible(el: HTMLElement, visible: boolean) {
    if (visible) this.visibleElements.add(el);
    else this.visibleElements.delete(el);
  }

  ref(tile: Tile, index = 0): (el: HTMLElement | null) => void {
    const { refs } = this;
    let refMap = refs.get(tile);
    if (!refMap) {
      refMap = new Map();
      refs.set(tile, refMap);
    }

    let info = refMap.get(index);
    if (!info) {
      const info2: TilesElementInfo = {
        setElement: (el: HTMLElement | null) => {
          info2.currentElement = el;
        },
        currentElement: null,
      };

      refMap.set(index, info2);
      info = info2;
    }

    return info.setElement;
  }

  *getVisibleTileElements(tile: Tile): Iterable<HTMLElement> {
    const infos = this.refs.get(tile)?.values();
    if (!infos) return;

    for (const info of infos) {
      const el = info.currentElement;
      if (el && this.visibleElements.has(el)) {
        yield el;
      }
    }
  }
}

function first<T>(it: Iterable<T>): T | undefined {
  for (const item of it) {
    return item;
  }

  return undefined;
}
